(() => {
  'use strict';
  if (window.OleaAssistantAttachments) return;
  const MAX_FILES = 2, MAX_INPUT = 8 * 1024 * 1024, MAX_OUTPUT = 1000000;
  const MAX_SIDE = 16000, MAX_PIXELS = 40000000, OUTPUT_SIDE = 1600;
  const defaults = {
    pickLabel: 'Add photos', removeLabel: 'Remove {name}',
    limitError: 'Attach up to two photos.', typeError: 'Choose a JPEG, PNG or WebP photo.',
    sizeError: 'Each original photo must be smaller than 8 MB.',
    dimensionsError: 'This photo has too many pixels. Choose a smaller version.',
    decodeError: 'This photo could not be opened.', compressError: 'This photo could not be prepared. Choose a smaller version.',
    processing: 'Preparing photos…', ready: '{count} photo(s) ready to send.', disabled: 'Photos are unavailable right now.'
  };
  const error = code => Object.assign(new Error(code), { code });
  const aborted = () => error('cancelled');
  const check = signal => { if (signal.aborted) throw aborted(); };
  const dimensions = (width, height) => {
    if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1 || width > MAX_SIDE || height > MAX_SIDE || width * height > MAX_PIXELS) throw error('dimensionsError');
  };
  function inspect(bytes, type) {
    const word = (offset, size) => String.fromCharCode(...bytes.subarray(offset, offset + size));
    let width, height;
    if (type === 'image/png') {
      const signature = [137, 80, 78, 71, 13, 10, 26, 10];
      if (bytes.length < 24 || signature.some((value, index) => bytes[index] !== value) || word(12, 4) !== 'IHDR') throw error('typeError');
      const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
      width = view.getUint32(16); height = view.getUint32(20);
    } else if (type === 'image/jpeg') {
      if (bytes.length < 4 || bytes[0] !== 255 || bytes[1] !== 216 || bytes[2] !== 255) throw error('typeError');
      let offset = 2;
      while (offset + 1 < bytes.length) {
        if (bytes[offset++] !== 255) throw error('decodeError');
        while (bytes[offset] === 255) offset++;
        const marker = bytes[offset++];
        if (marker === 217 || marker === 218) break;
        if (marker === 216 || marker === 1 || marker >= 208 && marker <= 215) continue;
        if (offset + 2 > bytes.length) throw error('decodeError');
        const length = bytes[offset] * 256 + bytes[offset + 1];
        if (length < 2 || offset + length > bytes.length) throw error('decodeError');
        if ([192, 193, 194, 195, 197, 198, 199, 201, 202, 203, 205, 206, 207].includes(marker)) {
          if (length < 8) throw error('decodeError');
          height = bytes[offset + 3] * 256 + bytes[offset + 4]; width = bytes[offset + 5] * 256 + bytes[offset + 6];
          break;
        }
        offset += length;
      }
      if (!width || !height) throw error('decodeError');
    } else if (type === 'image/webp') {
      if (bytes.length < 25 || word(0, 4) !== 'RIFF' || word(8, 4) !== 'WEBP') throw error('typeError');
      const chunk = word(12, 4);
      if (chunk === 'VP8X' && bytes.length >= 30) {
        width = 1 + bytes[24] + bytes[25] * 256 + bytes[26] * 65536;
        height = 1 + bytes[27] + bytes[28] * 256 + bytes[29] * 65536;
      } else if (chunk === 'VP8L' && bytes[20] === 47) {
        width = 1 + bytes[21] + ((bytes[22] & 63) << 8);
        height = 1 + (bytes[22] >> 6) + (bytes[23] << 2) + ((bytes[24] & 15) << 10);
      } else if (chunk === 'VP8 ' && bytes.length >= 30 && bytes[23] === 157 && bytes[24] === 1 && bytes[25] === 42) {
        width = (bytes[26] + bytes[27] * 256) & 16383; height = (bytes[28] + bytes[29] * 256) & 16383;
      } else throw error('decodeError');
    } else throw error('typeError');
    dimensions(width, height);
  }
  function create({ root, onChange, onStatus, strings = {} } = {}) {
    const input = root?.querySelector('input[type="file"]');
    const picker = root?.querySelector('[data-attachment-pick]');
    const list = root?.querySelector('[data-attachment-list]');
    if (!input || !picker || !list) throw new TypeError('Attachment input, picker and list are required.');
    const doc = root.ownerDocument || document;
    const events = new AbortController();
    const labels = { ...defaults, ...strings };
    let items = [], processing = false, disabled = false, disabledReason = '', destroyed = false, version = 0, sequence = 0, pending = null;
    input.accept = 'image/jpeg,image/png,image/webp'; input.multiple = true; picker.type = 'button';
    if (!picker.getAttribute('aria-label')) picker.setAttribute('aria-label', labels.pickLabel);
    list.setAttribute('role', 'list');
    const format = (key, values = {}) => String(labels[key] || defaults[key] || '').replace(/\{(name|count)\}/g, (match, name) => String(values[name] ?? ''));
    const images = () => items.map(({ dataUrl, name }) => ({ dataUrl, name, detail: 'low' }));
    const status = (message, kind = 'info') => { if (!destroyed) { try { onStatus?.(message, kind); } catch {} } };
    const changed = () => { if (!destroyed) { try { onChange?.({ busy: processing, count: items.length, images: images() }); } catch {} } };
    function render() {
      root.setAttribute('aria-busy', String(processing));
      input.disabled = picker.disabled = destroyed || disabled || processing;
      picker.setAttribute('aria-disabled', String(input.disabled));
      if (disabledReason) picker.setAttribute('title', disabledReason); else picker.removeAttribute('title');
      list.replaceChildren(...items.map(item => {
        const entry = doc.createElement('div'); entry.className = 'assistant-attachment'; entry.setAttribute('role', 'listitem');
        const preview = doc.createElement('img'); preview.className = 'assistant-attachment__preview'; preview.src = item.dataUrl; preview.alt = ''; preview.width = 48; preview.height = 48;
        const name = doc.createElement('span'); name.className = 'assistant-attachment__name'; name.textContent = item.name;
        const remove = doc.createElement('button'); remove.type = 'button'; remove.className = 'assistant-attachment__remove'; remove.dataset.attachmentRemove = item.id;
        remove.textContent = '×'; remove.setAttribute('aria-label', format('removeLabel', { name: item.name })); remove.disabled = processing;
        entry.append(preview, name, remove); return entry;
      }));
      list.hidden = items.length === 0;
    }
    function cancel() {
      version++; pending?.abort(); pending = null; processing = false;
    }
    function decode(file, signal) {
      check(signal);
      return new Promise((resolve, reject) => {
        const picture = new Image(); const url = URL.createObjectURL(file);
        let settled = false;
        const finish = (failure, result) => {
          if (settled) return; settled = true; clearTimeout(timer);
          signal.removeEventListener('abort', abort); picture.onload = picture.onerror = null;
          URL.revokeObjectURL(url);
          if (failure) { picture.src = ''; reject(failure); } else resolve(picture);
        };
        const abort = () => finish(aborted());
        const timer = setTimeout(() => finish(error('decodeError')), 10000);
        signal.addEventListener('abort', abort, { once: true });
        picture.onload = () => {
          try { check(signal); dimensions(picture.naturalWidth, picture.naturalHeight); finish(null, picture); }
          catch (failure) { finish(failure); }
        };
        picture.onerror = () => finish(error('decodeError'));
        picture.src = url;
      });
    }
    function encode(canvas, quality, signal) {
      check(signal);
      return new Promise((resolve, reject) => {
        let settled = false;
        const finish = (failure, blob) => {
          if (settled) return; settled = true; clearTimeout(timer); signal.removeEventListener('abort', abort);
          if (failure) reject(failure); else resolve(blob);
        };
        const abort = () => finish(aborted());
        const timer = setTimeout(() => finish(error('compressError')), 10000);
        signal.addEventListener('abort', abort, { once: true });
        try { canvas.toBlob(blob => finish(blob && blob.size && blob.type === 'image/jpeg' ? null : error('compressError'), blob), 'image/jpeg', quality); }
        catch { finish(error('compressError')); }
      });
    }
    function dataURL(blob, signal) {
      check(signal);
      return new Promise((resolve, reject) => {
        const reader = new FileReader(); let settled = false;
        const finish = (failure, value) => {
          if (settled) return; settled = true; clearTimeout(timer); signal.removeEventListener('abort', abort);
          reader.onload = reader.onerror = reader.onabort = null;
          if (failure) reject(failure); else resolve(value);
        };
        const abort = () => { finish(aborted()); try { reader.abort(); } catch {} };
        const timer = setTimeout(() => { finish(error('compressError')); try { reader.abort(); } catch {} }, 10000);
        signal.addEventListener('abort', abort, { once: true });
        reader.onload = () => {
          const result = reader.result;
          if (typeof result !== 'string' || !/^data:image\/jpeg;base64,[a-zA-Z0-9+/]+={0,2}$/.test(result) || result.length > 1400000) finish(error('compressError'));
          else finish(null, result);
        };
        reader.onerror = () => finish(error('compressError'));
        reader.onabort = () => finish(aborted());
        try { reader.readAsDataURL(blob); } catch { finish(error('compressError')); }
      });
    }
    async function prepare(file, signal) {
      check(signal);
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) throw error('typeError');
      if (!file.size || file.size > MAX_INPUT) throw error('sizeError');
      const bytes = new Uint8Array(await file.arrayBuffer()); check(signal); inspect(bytes, file.type);
      const picture = await decode(file, signal); check(signal);
      const canvas = doc.createElement('canvas'); const context = canvas.getContext('2d');
      if (!context) { picture.src = ''; throw error('compressError'); }
      try {
        let side = Math.min(OUTPUT_SIDE, Math.max(picture.naturalWidth, picture.naturalHeight));
        for (let pass = 0; pass < 5; pass++) {
          check(signal);
          const scale = side / Math.max(picture.naturalWidth, picture.naturalHeight);
          canvas.width = Math.max(1, Math.round(picture.naturalWidth * scale)); canvas.height = Math.max(1, Math.round(picture.naturalHeight * scale));
          context.fillStyle = '#fff'; context.fillRect(0, 0, canvas.width, canvas.height);
          context.drawImage(picture, 0, 0, canvas.width, canvas.height);
          for (const quality of [.88, .76, .64, .5, .4]) {
            const blob = await encode(canvas, quality, signal); check(signal);
            if (blob.size <= MAX_OUTPUT) return { id: String(++sequence), name: String(file.name || 'Photo').slice(0, 100), dataUrl: await dataURL(blob, signal) };
          }
          side = Math.max(1, Math.floor(side * .75));
        }
        throw error('compressError');
      } finally { canvas.width = canvas.height = 0; picture.src = ''; }
    }
    async function choose(files) {
      if (destroyed || !files.length) return;
      if (disabled) { status(disabledReason || format('disabled')); return; }
      if (processing) return;
      if (items.length + files.length > MAX_FILES) { status(format('limitError'), 'error'); return; }
      const ticket = ++version; const controller = new AbortController(); pending = controller;
      processing = true; render(); changed();
      if (controller.signal.aborted || ticket !== version || destroyed) return;
      status(format('processing'));
      try {
        const next = [];
        for (const file of files) next.push(await prepare(file, controller.signal));
        check(controller.signal);
        if (ticket !== version || destroyed) return;
        const combined = [...items, ...next];
        if (JSON.stringify(combined.map(({ dataUrl, name }) => ({ dataUrl, name, detail: 'low' }))).length > 3000000) throw error('compressError');
        items = combined; status(format('ready', { count: items.length }));
      } catch (failure) {
        if (ticket === version && !destroyed && failure.code !== 'cancelled') status(format(failure.code in defaults ? failure.code : 'decodeError'), 'error');
      } finally {
        if (ticket === version && !destroyed) { pending = null; processing = false; render(); changed(); }
      }
    }
    picker.addEventListener('click', () => { if (!disabled && !processing && !destroyed) input.click(); }, { signal: events.signal });
    input.addEventListener('change', () => { const files = Array.from(input.files || []); input.value = ''; void choose(files); }, { signal: events.signal });
    list.addEventListener('click', event => {
      const button = event.target.closest('[data-attachment-remove]');
      if (!button || !list.contains(button) || processing || destroyed) return;
      items = items.filter(item => item.id !== button.dataset.attachmentRemove); render(); changed();
      status(items.length ? format('ready', { count: items.length }) : '');
      const fallback = picker.disabled ? root.closest('form')?.querySelector('textarea:not(:disabled)') : picker;
      (list.querySelector('[data-attachment-remove]') || fallback)?.focus();
    }, { signal: events.signal });
    render();
    return {
      getImages: images,
      get busy() { return processing; },
      clear() { if (!destroyed) { cancel(); items = []; input.value = ''; render(); changed(); status(''); } },
      setDisabled(value, reason = '') {
        if (destroyed) return;
        const next = Boolean(value), message = next ? String(reason || '') : '';
        const updated = next !== disabled || message !== disabledReason;
        if (!updated) return;
        disabled = next; disabledReason = message;
        if (disabled && processing) { cancel(); changed(); }
        render(); if (updated && disabled && message) status(message);
      },
      destroy() { if (!destroyed) { cancel(); destroyed = true; events.abort(); items = []; input.value = ''; render(); } }
    };
  }
  window.OleaAssistantAttachments = { create };
})();
